import { useEffect, useState } from 'react';

import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import _debounce from 'lodash.debounce';

import { ContactType, Permissions } from '@cloud-wave/neon-common-lib';

import { useContactContext } from 'lib/common/contexts/ContactContext';
import { TTransferDestination } from 'lib/common/contexts/OverlayContext/Context';
import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';
import { useSoftphoneModalContext } from 'lib/common/contexts/SoftphoneModalContext';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import Button from 'lib/common/components/Button';
import SegmentedControls from 'lib/common/components/SegmentedControls';

import CONTACT_STATES from 'lib/common/constants/contactStates';

import InfoPanel, { PanelTypes } from '../../atoms/InfoPanel';
import ContactList from './components/ContactList';
import useTransfers from './components/ContactList/hooks/useTransfers';
import CreateUpdateDirectoryContact from './components/CreateUpdateDirectoryContact';
import Searchbar from './components/Searchbar/Searchbar';
import { DIRECTORY_TAB } from './constants/directoryTabs';
import { TInternalContacts } from './types';
import getTabOptions from './utils/getTabOptions';

import styles from './directory.module.scss';

const SEARCH_DEBOUNCE_TIME = 700;

export interface DirectoryProps {
  initialSelectedTab?: DIRECTORY_TAB;
  transferTo?: TTransferDestination;
  open?: boolean;
  onClose?: () => void;
  pushToTask?: (url: string) => void;
  resetTitle?: () => void;
  setTitle?: (title: string) => void;
  isDrawer?: boolean;
}

enum DirectoryPages {
  List,
  CreateUpdate,
}

const Directory = ({
  initialSelectedTab = DIRECTORY_TAB.AGENTS,
  transferTo,
  open,
  onClose,
  setTitle,
  resetTitle,
  isDrawer = true,
}: DirectoryProps) => {
  const [page, setPage] = useState(DirectoryPages.List);

  const {
    actions: { getSelectedTask },
  } = useContactContext();
  const [currentTab, setCurrentTab] = useState(initialSelectedTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [contact, setContact] = useState<TInternalContacts | undefined>(undefined);
  const { isSoftphone } = useLayout();
  const { hasPermission, isAdmin } = usePermissionsContext();
  const {
    actions: { openModal, closeModal },
  } = useSoftphoneModalContext();

  const queryClient = useQueryClient();
  const invalidateQueryMap = {
    [DIRECTORY_TAB.AGENTS]: { queryKey: ['agents', searchTerm] },
    [DIRECTORY_TAB.ORGANISATION]: {
      queryKey: ['contacts', DIRECTORY_TAB.ORGANISATION, searchTerm],
    },
    [DIRECTORY_TAB.PERSONAL]: { queryKey: ['contacts', DIRECTORY_TAB.PERSONAL, searchTerm] },
  };

  // Clear query caches when component mounts, always get latest on mount
  useEffect(() => {
    Object.keys(invalidateQueryMap).map((it) => queryClient.removeQueries(invalidateQueryMap[it]));
  }, []);

  // Function to invalidate the correct query based on current tab
  // The child component uses usePaginatedLoad.
  // We need to specifically invalidate query directly from queryClient,
  // as accessing child component scope would require usage of forwardRef making it more complex
  const invalidateQuery = async () =>
    await queryClient.invalidateQueries({ ...invalidateQueryMap[currentTab], refetchType: 'all' });

  useEffect(() => {
    if (open === false) {
      setSearchTerm('');
      setPage(DirectoryPages.List);
      resetTitle?.();
    }
  }, [open]);

  const { transferringToId, onTransfer } = useTransfers({
    closeModal: onClose,
    isSoftphone,
  });

  const hasPersonalContactPermission = hasPermission({
    type: 'tenant',
    permission: Permissions.PERSONAL_CONTACTS,
  });

  const tabOptions = getTabOptions({
    hasPersonalContactPermission,
    isSoftphone,
    transferTo,
  });

  const canCreateContact =
    (!transferTo && currentTab === DIRECTORY_TAB.PERSONAL) ||
    (!transferTo && currentTab === DIRECTORY_TAB.ORGANISATION && isAdmin);

  const handleSearch = _debounce((toSearch: string) => {
    setSearchTerm(toSearch);
  }, SEARCH_DEBOUNCE_TIME);

  const selectedTask = getSelectedTask();
  const isTaskInactive = !selectedTask || selectedTask?.status === CONTACT_STATES.ACW;

  const onCreateOrUpdateContactClick = (contact?: TInternalContacts) => {
    const title = contact ? `Update ${contact.firstName} ${contact.lastName}` : 'Create a New Contact';

    if (isSoftphone) {
      return void openModal({
        title,
        content: (
          <CreateUpdateDirectoryContact
            contact={contact}
            onCancel={closeModal}
            onSuccess={async () => {
              await invalidateQuery();
              closeModal();
            }}
            defaultType={currentTab === DIRECTORY_TAB.ORGANISATION ? ContactType.ORGANISATION : ContactType.PERSONAL}
          />
        ),
      });
    }

    setTitle?.(title);
    setContact(contact);
    setPage(DirectoryPages.CreateUpdate);
  };

  if (!isSoftphone && page === DirectoryPages.CreateUpdate) {
    return (
      <CreateUpdateDirectoryContact
        onCancel={() => {
          resetTitle?.();
          setPage(DirectoryPages.List);
        }}
        onSuccess={async () => {
          await invalidateQuery();
          resetTitle?.();
          setPage(DirectoryPages.List);
        }}
        contact={contact}
        defaultType={currentTab === DIRECTORY_TAB.ORGANISATION ? ContactType.ORGANISATION : ContactType.PERSONAL}
      />
    );
  }

  return (
    <div className={cx(styles['directory'], { [styles['directory--softphone']]: isSoftphone })}>
      <div className={styles['directory__search']} data-testid="directory__search">
        <Searchbar onChange={handleSearch} searchTerm={searchTerm} />
        {canCreateContact && (
          <Button
            ariaLabel="Create contact"
            icon={faPlus}
            data-testid="create-contact"
            size="medium"
            onClick={() => onCreateOrUpdateContactClick()}
          />
        )}
      </div>
      {tabOptions.length > 1 && (
        <SegmentedControls
          ariaLabel="Contact type"
          initialIndices={tabOptions.findIndex((tab) => tab.label === currentTab)}
          onSelect={(tabName: string) => setCurrentTab(tabName as DIRECTORY_TAB)}
          options={tabOptions}
        />
      )}
      {isTaskInactive && transferTo && !transferringToId && (
        <InfoPanel className={styles['directory__banner']} type={PanelTypes.WARNING}>
          You can no longer transfer as the task has ended.
        </InfoPanel>
      )}
      <ContactList
        isDrawer={isDrawer}
        onCreateOrUpdateClick={onCreateOrUpdateContactClick}
        onDeleteContact={async () => {
          await invalidateQuery();
        }}
        key={`${currentTab}${searchTerm}`}
        selectedTab={currentTab}
        searchTerm={searchTerm}
        onClearSearchTerm={() => setSearchTerm('')}
        isAdmin={isAdmin}
        isTransfer={Boolean(transferTo)}
        transferDisabled={isTaskInactive}
        transferringToId={transferringToId}
        onTransfer={onTransfer}
      />
    </div>
  );
};

export { Directory };
